import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
const Container = styled.div`
  display: flex;
  align-items: flex-end;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  background-color: white;
  gap: 8px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;
const TextArea = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 20px;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #007bff;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;
const SendButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: background-color 0.2s, opacity 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:active:not(:disabled) {
    background-color: #004494;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
  }
`;
const CharCounter = styled.span`
  font-size: 12px;
  color: ${(props)=>props.isOverLimit ? '#dc3545' : '#666'};
  align-self: flex-end;
  padding-bottom: 10px;
  min-width: 50px;
  text-align: right;

  @media (max-width: 768px) {
    display: none;
  }
`;
const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;
/**
 * MessageInput Component
 * Text input for composing and sending messages with typing indicators
 */ export const MessageInput = ({ onSend, onTyping, maxLength = 5000, placeholder = 'Type a message... (Ctrl+Enter to send)', disabled = false })=>{
    const [content, setContent] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const typingTimeoutRef = useRef(null);
    const textAreaRef = useRef(null);
    const handleChange = (e)=>{
        const newContent = e.target.value;
        // Don't allow input beyond max length
        if (newContent.length > maxLength) {
            return;
        }
        setContent(newContent);
        // Auto-resize textarea
        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto';
            textAreaRef.current.style.height = `${Math.min(textAreaRef.current.scrollHeight, 120)}px`;
        }
        // Typing indicator logic
        if (!isTyping && newContent.length > 0) {
            setIsTyping(true);
            onTyping?.(true);
        }
        // Reset typing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(()=>{
            setIsTyping(false);
            onTyping?.(false);
        }, 3000);
    };
    const handleSend = useCallback(()=>{
        const trimmedContent = content.trim();
        if (trimmedContent && trimmedContent.length <= maxLength && !disabled) {
            onSend(trimmedContent);
            setContent('');
            setIsTyping(false);
            onTyping?.(false);
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            // Reset textarea height
            if (textAreaRef.current) {
                textAreaRef.current.style.height = 'auto';
            }
        }
    }, [
        content,
        maxLength,
        disabled,
        onSend,
        onTyping
    ]);
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSend();
        }
    };
    useEffect(()=>{
        return ()=>{
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, []);
    const isOverLimit = content.length > maxLength;
    const isSendDisabled = !content.trim() || isOverLimit || disabled;
    return /*#__PURE__*/ _jsxs(Container, {
        "data-testid": "message-input-container",
        children: [
            /*#__PURE__*/ _jsxs(InputWrapper, {
                children: [
                    /*#__PURE__*/ _jsx(TextArea, {
                        ref: textAreaRef,
                        value: content,
                        onChange: handleChange,
                        onKeyDown: handleKeyDown,
                        placeholder: placeholder,
                        disabled: disabled,
                        "data-testid": "chat-message-input",
                        rows: 1
                    }),
                    /*#__PURE__*/ _jsxs(CharCounter, {
                        isOverLimit: isOverLimit,
                        "data-testid": "chat-char-count",
                        children: [
                            content.length,
                            "/",
                            maxLength
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SendButton, {
                onClick: handleSend,
                disabled: isSendDisabled,
                "data-testid": "chat-send-button",
                children: "Send"
            })
        ]
    });
};
